import { styled } from 'styled-components'
import { Class, renameClass } from '../../../libs/class-service'
import { MdEdit } from 'react-icons/md'
import { IoArrowBack } from 'react-icons/io5'
import { useEffect, useState } from 'react'
import getStudents, {
    Student,
    removeStudentFromClass,
} from '../../../libs/student-service'
import { DoubleButtonModal } from '../../shared/Modal'
import EditStudentModal from './EditStudentModal'
import AddStudentModal from './AddStudentModal'
import RenameClassModal from './RenameClassModal'
import StudentUploadModal from './StudentUploadModal'

interface Props {
    classItem: Class
    onBack: () => void
}

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 30px;
`

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
`

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: none;
    background-color: transparent;
    color: #666666;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        color: #333333;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`

const Title = styled.h1`
    font-size: 28px;
    font-weight: 600;
    color: #000000;
    margin: 0;
`

const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`

const RenameButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: none;
    background-color: transparent;
    color: #1fa474;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        color: #168a5d;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    background-color: #ffffff;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #e5e6eb;
`

const SectionHeading = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`

const SectionTitle = styled.h2`
    font-size: 18px;
    font-weight: 600;
    color: #000000;
    margin: 0;
`

const AddButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    background-color: #ffffff;
    color: #666666;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #f2f3f5;
    }
`

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`

const TableWrapper = styled.div`
    overflow-x: auto;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    thead {
        background-color: #f5f5f5;
    }
`

const TH = styled.th`
    text-align: left;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #333333;
    border-bottom: 1px solid #e5e6eb;
`

const TD = styled.td`
    padding: 12px 16px;
    font-size: 14px;
    color: #333333;
    border-bottom: 1px solid #e5e6eb;
`

const ActionCell = styled.td`
    padding: 12px 16px;
    border-bottom: 1px solid #e5e6eb;
    display: flex;
    gap: 8px;
    align-items: center;
`

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &.assess {
        background-color: transparent;
        color: #1fa474;

        &:hover {
            background-color: #e8f5f0;
        }
    }

    &.remove {
        background-color: transparent;
        color: #ff9500;

        &:hover {
            background-color: #fff3e0;
        }
    }

    &.edit {
        background-color: transparent;
        color: #1fa474;

        &:hover {
            background-color: #e8f5f0;
        }
    }

    svg {
        width: 14px;
        height: 14px;
    }
`

const PaginationContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e5e6eb;
`

const PaginationButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid #e5e6eb;
    border-radius: 4px;
    background-color: #ffffff;
    color: #333333;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background-color: #f2f3f5;
        border-color: #333333;
    }

    &.active {
        background-color: #1fa474;
        color: #ffffff;
        border-color: #1fa474;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`

const Empty = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: #999999;
    font-size: 14px;
`

export default function ClassDetail({ classItem, onBack }: Props) {
    const [students, setStudents] = useState<Student[]>([])
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [showRemoveModal, setShowRemoveModal] = useState(false)
    const [studentToRemove, setStudentToRemove] = useState<Student | null>(null)
    const [showEditModal, setShowEditModal] = useState(false)
    const [studentToEdit, setStudentToEdit] = useState<Student | null>(null)
    const [showAddModal, setShowAddModal] = useState(false)
    const [showRenameModal, setShowRenameModal] = useState(false)
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [className, setClassName] = useState(classItem.class_name)
    const itemsPerPage = 8

    useEffect(() => {
        setClassName(classItem.class_name)
    }, [classItem.id])

    useEffect(() => {
        fetchStudents()
    }, [classItem.id])

    const fetchStudents = async () => {
        try {
            setLoading(true)
            // Using batch_id 0 as a default - adjust based on your API requirements
            const response = await getStudents(classItem.id, 0)
            setStudents(response.students || [])
        } catch (error) {
            console.error('Failed to fetch students:', error)
            setStudents([])
        } finally {
            setLoading(false)
        }
    }

    const totalPages = Math.ceil(students.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const paginatedStudents = students.slice(
        startIndex,
        startIndex + itemsPerPage
    )

    const handleManageStudent = (student: Student) => {
        setStudentToEdit(student)
        setShowEditModal(true)
    }

    const handleRemoveStudent = (student: Student) => {
        setStudentToRemove(student)
        setShowRemoveModal(true)
    }

    const handleConfirmRemove = async () => {
        if (!studentToRemove) return

        try {
            await removeStudentFromClass(classItem.id, studentToRemove.id)
            // Remove the student from the state
            setStudents((prevStudents) =>
                prevStudents.filter((s) => s.id !== studentToRemove.id)
            )
            setShowRemoveModal(false)
            setStudentToRemove(null)
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to remove student'
            alert(`Error: ${errorMessage}`)
            console.error('Failed to remove student:', error)
            setShowRemoveModal(false)
            setStudentToRemove(null)
        }
    }

    const handleCancelRemove = () => {
        setShowRemoveModal(false)
        setStudentToRemove(null)
    }

    const handleEditStudentClose = () => {
        setShowEditModal(false)
        setStudentToEdit(null)
    }

    const handleEditStudentConfirm = (updatedData: Partial<Student>) => {
        if (!studentToEdit) return

        try {
            // Update the student in the local state
            setStudents((prevStudents) =>
                prevStudents.map((s) =>
                    s.id === studentToEdit.id ? { ...s, ...updatedData } : s
                )
            )
            handleEditStudentClose()
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to update student'
            alert(`Error: ${errorMessage}`)
            console.error('Failed to update student:', error)
        }
    }

    const handleAddStudent = () => {
        setShowAddModal(true)
    }

    const handleAddStudentClose = () => {
        setShowAddModal(false)
    }

    const handleAddStudentConfirm = () => {
        fetchStudents()
        handleAddStudentClose()
    }

    const handleUploadStudents = () => {
        setShowUploadModal(true)
    }

    const handleUploadStudentsClose = () => {
        setShowUploadModal(false)
    }

    const handleUploadStudentsConfirm = () => {
        fetchStudents()
        handleUploadStudentsClose()
    }

    const handleRenameClick = () => {
        setShowRenameModal(true)
    }

    const handleRenameClose = () => {
        setShowRenameModal(false)
    }

    const handleRenameConfirm = async (newName: string) => {
        try {
            await renameClass(classItem.id, newName)
            setClassName(newName)
            handleRenameClose()
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to rename class'
            alert(`Error: ${errorMessage}`)
            console.error('Failed to rename class:', error)
        }
    }

    const renderPagination = () => {
        const pages = []
        const maxVisible = 5

        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
        let endPage = Math.min(totalPages, startPage + maxVisible - 1)

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1)
        }

        if (startPage > 1) {
            pages.push(
                <PaginationButton
                    key="prev"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                >
                    ←
                </PaginationButton>
            )
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <PaginationButton
                    key={i}
                    className={currentPage === i ? 'active' : ''}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </PaginationButton>
            )
        }

        if (endPage < totalPages) {
            pages.push(
                <PaginationButton
                    key="next"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                >
                    →
                </PaginationButton>
            )
        }

        return pages
    }

    return (
        <Container>
            <Header>
                <BackButton onClick={onBack}>
                    <IoArrowBack />
                    My Classes
                </BackButton>
            </Header>

            <TopBar>
                <Title>{className}</Title>
                <RenameButton onClick={handleRenameClick}>
                    <MdEdit />
                    Rename
                </RenameButton>
            </TopBar>

            <ContentSection>
                <SectionHeading>
                    <SectionTitle>Students</SectionTitle>
                    <ButtonGroup>
                        <AddButton onClick={handleAddStudent}>
                            + Add Student
                        </AddButton>
                        <AddButton onClick={handleUploadStudents}>
                            Upload Students
                        </AddButton>
                    </ButtonGroup>
                </SectionHeading>

                {loading ? (
                    <Empty>Loading students...</Empty>
                ) : students.length === 0 ? (
                    <Empty>No students in this class yet</Empty>
                ) : (
                    <>
                        <TableWrapper>
                            <Table>
                                <thead>
                                    <tr>
                                        <TH>Name</TH>
                                        <TH>ID</TH>
                                        <TH style={{ width: '300px' }}>
                                            Actions
                                        </TH>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedStudents.map((student) => (
                                        <tr key={student.id}>
                                            <TD>
                                                {student.first_name}{' '}
                                                {student.last_name}
                                            </TD>
                                            <TD>{student.student_id}</TD>
                                            <ActionCell>
                                                <ActionButton
                                                    className="remove"
                                                    onClick={() =>
                                                        handleRemoveStudent(
                                                            student
                                                        )
                                                    }
                                                >
                                                    Remove
                                                </ActionButton>
                                                <ActionButton
                                                    className="edit"
                                                    onClick={() =>
                                                        handleManageStudent(
                                                            student
                                                        )
                                                    }
                                                >
                                                    <MdEdit />
                                                    Edit
                                                </ActionButton>
                                            </ActionCell>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </TableWrapper>

                        {totalPages > 1 && (
                            <PaginationContainer>
                                {renderPagination()}
                            </PaginationContainer>
                        )}
                    </>
                )}
            </ContentSection>

            {showRemoveModal && studentToRemove && (
                <DoubleButtonModal
                    onCancel={handleCancelRemove}
                    onConfrim={handleConfirmRemove}
                >
                    <div>
                        Are you sure you want to remove{' '}
                        <strong>
                            {studentToRemove.first_name}{' '}
                            {studentToRemove.last_name}
                        </strong>{' '}
                        from this class?
                    </div>
                </DoubleButtonModal>
            )}

            {showEditModal && studentToEdit && (
                <EditStudentModal
                    student={studentToEdit}
                    onClose={handleEditStudentClose}
                    onConfirm={handleEditStudentConfirm}
                />
            )}

            {showAddModal && (
                <AddStudentModal
                    classId={classItem.id}
                    onClose={handleAddStudentClose}
                    onConfirm={handleAddStudentConfirm}
                />
            )}

            {showRenameModal && (
                <RenameClassModal
                    currentName={className}
                    onClose={handleRenameClose}
                    onConfirm={handleRenameConfirm}
                />
            )}

            {showUploadModal && (
                <StudentUploadModal
                    classId={classItem.id}
                    onClose={handleUploadStudentsClose}
                    onConfirm={handleUploadStudentsConfirm}
                />
            )}
        </Container>
    )
}
